import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Plus, Search, Loader2, HardHat, Eye, EyeOff, ArrowRight, Trash2,
} from "lucide-react";
import { toast } from "sonner";

export default function ChantiersAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [published, setPublished] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (published) params.set("published", published);
      const r = await api.get(`/jardins-ole/admin/crm/worksites?${params.toString()}`);
      setItems(r.data || []);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, published]);

  const remove = async (w) => {
    if (!window.confirm(`Supprimer le chantier "${w.title}" ? (la réalisation liée sera également retirée)`)) return;
    try {
      await api.delete(`/jardins-ole/admin/crm/worksites/${w.id}`);
      toast.success("Chantier supprimé");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="chantiers-admin">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <HardHat className="w-6 h-6" /> Chantiers
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Suivi des interventions chantier et publication anonymisée vers les Réalisations.
          </p>
        </div>
        <Link to="/admin/chantiers/new">
          <Button data-testid="new-chantier-btn"><Plus className="w-4 h-4 mr-2" />Nouveau chantier</Button>
        </Link>
      </div>

      <Card className="p-4 flex flex-col md:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par titre, ville, prestation, contexte..."
            className="pl-9"
            data-testid="chantier-search-input"
          />
        </div>
        <select
          value={published}
          onChange={(e) => setPublished(e.target.value)}
          className="border border-[hsl(var(--border))] rounded-md px-3 h-10 text-sm bg-white"
          data-testid="chantier-publish-filter"
        >
          <option value="">Tous</option>
          <option value="yes">Publiés</option>
          <option value="no">Brouillons</option>
        </select>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-[hsl(var(--muted-foreground))]">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement...
        </div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center">
          <HardHat className="w-12 h-12 mx-auto text-[hsl(var(--muted-foreground))]/40 mb-3" />
          <div className="text-[hsl(var(--muted-foreground))] mb-4">Aucun chantier pour le moment.</div>
          <Link to="/admin/chantiers/new">
            <Button><Plus className="w-4 h-4 mr-2" />Créer un chantier</Button>
          </Link>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))]/40 text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                <tr>
                  <th className="px-4 py-3 text-left">Chantier</th>
                  <th className="px-4 py-3 text-left">Client</th>
                  <th className="px-4 py-3 text-left">Ville</th>
                  <th className="px-4 py-3 text-left">Durée</th>
                  <th className="px-4 py-3 text-left">Photos</th>
                  <th className="px-4 py-3 text-left">Statut</th>
                  <th className="px-4 py-3 text-right w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((w) => (
                  <tr key={w.id} className="border-t hover:bg-[hsl(var(--muted))]/20" data-testid={`chantier-row-${w.id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/admin/chantiers/${w.id}`} className="font-medium text-[color:var(--brand-navy)] hover:underline">
                        {w.title}
                      </Link>
                      <div className="text-xs text-[hsl(var(--muted-foreground))]">{w.service_type || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{w.client_label || "—"}</td>
                    <td className="px-4 py-3 text-xs">{w.location_city || "—"}</td>
                    <td className="px-4 py-3 text-xs">{w.duration_days || 0} j</td>
                    <td className="px-4 py-3 text-xs">
                      {(w.before_photos || []).length} avant · {(w.after_photos || []).length} après
                    </td>
                    <td className="px-4 py-3">
                      {w.is_published ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold inline-flex items-center gap-1">
                          <Eye className="w-3 h-3" /> Publié
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1">
                          <EyeOff className="w-3 h-3" /> Brouillon
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/admin/chantiers/${w.id}`}>
                        <Button size="sm" variant="ghost"><ArrowRight className="w-4 h-4" /></Button>
                      </Link>
                      <Button size="sm" variant="ghost" onClick={() => remove(w)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
